import { setHoveredElementKey } from "@/store/iframeSlice";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import type { Storyboard as StoryboardType } from "@/utils/types";
import { useCanvasId } from "@/utils/utils";
import { Loader2 } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon, { ICONS } from "../../Core/Icons";
import type {
  CanvasItemRef,
  CanvasItemState,
  SimpleCanvasRef,
} from "../../SimpleCanvas/SimpleCanvas";
import { Skeleton } from "../../ui/skeleton";
import IframeComponent from "./StaticIframe/StaticIframeComponent";
import { StoryboardControlsOverlay } from "./StoryboardControlsOverlay";
import { useStoryboardUrl } from "../hooks/useStoryboardUrl";
import { useStoryboardRegistration } from "../IframePool/useStoryboardRegistration";
import OutlineSystem, { type OutlineSystemRef } from "./OutlineSystem";
import { CANVAS_BACKGROUND_COLOR, STORYBOARD_TYPES } from "@/utils/constants";
import { TEMP_ELEMENT_DRAG_STORYBOARD_ID } from "@/components/SimpleCanvas/SimpleCanvas";
import DashedBorder from "../../ui/DashedBorder";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import ErrorOverlay from "../ErrorOverlay/ErrorOverlay";
import { useStoryboardState } from "./StaticIframe/hooks/useStoryboardState";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import StoryboardLongLoading from "./StoryboardLongLoading";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import useToggleDriveMode from "@/components/AppHeader/hooks/useToggleDriveMode";
import { CursorType } from "@/store/globalSettingsSlice";
import { TempoElement } from "@/utils/tempoElement";
import { useStylingPanelStore } from "@/components/RightStyle/TailwindStylingPanel/stylingPanelStore";
import { PropertyType } from "@/tailwind/tailwindUtils";

// Export the interface for the imperative handle
export interface StoryboardRef extends CanvasItemRef {
  getIframeDocument: () => Document | null;
}

interface StoryboardProps {
  storyboard: StoryboardType;
  canvasItemState: CanvasItemState;
  resizeDomElement?: HTMLElement;
  index?: number;
  anyErrorForThisStoryboard?: boolean;
  refreshStoryboard?: () => void;
  simpleCanvasRef: React.RefObject<SimpleCanvasRef>;
  registerRef?: (ref: CanvasItemRef | null) => void;
}

export const Storyboard = forwardRef<StoryboardRef, StoryboardProps>(
  (
    {
      storyboard,
      canvasItemState,
      resizeDomElement,
      index = 0,
      simpleCanvasRef,
      registerRef,
    },
    ref,
  ) => {
    const canvasId = useCanvasId();

    const frozenIframeRef = useRef<HTMLIFrameElement | null>(null);

    const { storyboardIdHovered } = useHoveredNavTreeNode();
    const storyboardHovered = storyboardIdHovered === storyboard?.id;

    const storyboardRef = useRef<StoryboardType | null>(null);
    useEffect(() => {
      storyboardRef.current = storyboard;
    }, [storyboard]);

    const containerRef = useRef<HTMLDivElement>(null);

    // State for HTML content from iframe pool
    const htmlForThisStoryboard = useIframePoolStore(
      (state) => state.htmlForStoryboard[storyboard.id],
    );

    const {
      error: storyboardError,
      slowToLoad,
      longLoading,
    } = useStoryboardState(storyboard);

    const devServerUrl = useSelector(
      (state: any) => state.iframe.canvasIdToDevServerUrl?.[canvasId],
    );

    const navTreeForStoryboard = useCanvasStore(
      (state) => state.storyboardToNavTree?.[storyboard.id],
    );

    const nonApplicationRegisteredStoryboard = useIframePoolStore(
      (state) => state.registeredStoryboards[storyboard.id],
    );
    const applicationRegisteredStoryboard = useIframePoolStore(
      (state) => state.applicationStoryboards[storyboard.id],
    );

    const outlineSystemRef = useRef<OutlineSystemRef | null>(null);
    const [isResizing, setIsResizing] = useState(false);

    const dispatch = useDispatch();

    const cursorType = useSelector(
      (state: any) => state.globalSettings.cursorType,
    );
    const isDriveMode = cursorType === CursorType.Drive;

    const registeredStoryboard =
      storyboard.type === STORYBOARD_TYPES.APPLICATION
        ? applicationRegisteredStoryboard
        : nonApplicationRegisteredStoryboard;

    const { toggleCursor } = useToggleDriveMode();

    // Register with iframe pool to automatically get HTML updates
    useStoryboardRegistration(storyboard);

    // ============================================================================
    // IMPERATIVE HANDLE
    // ============================================================================
    const storyboardImperativeRef: StoryboardRef = useMemo(
      () => ({
        onResizeInstantUpdate: (rect) => {
          if (
            !storyboardRef.current ||
            storyboardRef.current.type === STORYBOARD_TYPES.APPLICATION
          ) {
            outlineSystemRef.current?.refreshOutlines();
            return;
          }

          // Also, update the instant update overrides for the tailwind styling panel (width/height)
          useStylingPanelStore
            .getState()
            .setTemporaryFieldDisplayOverride(
              PropertyType.WIDTH,
              `${Math.round(rect.width)}px`,
            );
          useStylingPanelStore
            .getState()
            .setTemporaryFieldDisplayOverride(
              PropertyType.HEIGHT,
              `${Math.round(rect.height)}px`,
            );

          outlineSystemRef.current?.refreshOutlines();
        },
        onResizeEnd: () => {
          outlineSystemRef.current?.refreshOutlines();
        },
        getIframeDocument: () => {
          // Return the iframe document if available
          if (frozenIframeRef.current) {
            return (
              frozenIframeRef.current.contentDocument ||
              frozenIframeRef.current.contentWindow?.document ||
              null
            );
          }
          return null;
        },
      }),
      [],
    );

    useEffect(() => {
      // Clear, they are now directly updated
      useStylingPanelStore
        .getState()
        .setTemporaryFieldDisplayOverride(PropertyType.WIDTH, undefined);
      useStylingPanelStore
        .getState()
        .setTemporaryFieldDisplayOverride(PropertyType.HEIGHT, undefined);
    }, [storyboard.width, storyboard.height]);

    useImperativeHandle(ref, () => storyboardImperativeRef, [
      storyboardImperativeRef,
    ]);

    // Register this ref with the parent
    useEffect(() => {
      if (registerRef) {
        registerRef(storyboardImperativeRef);
        return () => registerRef(null);
      }
    }, [registerRef, storyboardImperativeRef]);

    // ============================================================================
    // COMPUTED VALUES
    // ============================================================================

    // URL calculations
    const storyboardUrl = useStoryboardUrl(storyboard);

    const domainFromStoryboardUrl = devServerUrl
      ?.split("/")
      ?.slice(0, 3)
      ?.join("/");

    // ============================================================================
    // IFRAME RENDERING
    // ============================================================================

    const renderMainFrame = () => {
      // Priority 1: Generating state
      if (storyboard.renderStatus === "generating") {
        return (
          <div
            className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-sm flex-col"
            style={{
              width: storyboard.width || 0,
              height: storyboard.height || 0,
              pointerEvents: "none",
              backgroundColor: CANVAS_BACKGROUND_COLOR,
            }}
          >
            <Skeleton className="w-full h-full absolute top-0 left-0" />
            <Icon
              icon={ICONS.AI_LOGO}
              width={48}
              height={48}
              className="opacity-10"
            />
            <div>Generating</div>
          </div>
        );
      }

      // Priority 2: Placeholder state
      if (
        storyboard.renderStatus === "placeholder" &&
        storyboard.designSuggestionId
      ) {
        return (
          <DashedBorder
            dashLength={12}
            gapLength={12}
            borderWidth={5}
            borderColor={"#6448ef"}
            borderRadius={6}
            style={{
              width: storyboard.width || 0,
              height: storyboard.height || 0,
              backgroundColor: CANVAS_BACKGROUND_COLOR,
              pointerEvents: "none",
            }}
          />
        );
      }

      // Priority 4: Always show the frozen storyboard HTML if available
      if (htmlForThisStoryboard) {
        const opacity =
          isDriveMode &&
          (storyboard?.type === STORYBOARD_TYPES.APPLICATION ||
            longLoading ||
            storyboardError)
            ? 0
            : 1;

        return (
          <IframeComponent
            ref={(ref) => {
              if (ref) {
                frozenIframeRef.current = ref.ref;
              } else {
                frozenIframeRef.current = null;
              }
            }}
            key={`${storyboard.id}-frozen-state`}
            className="tempo-component-storyboard-iframe"
            style={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
              zIndex: 1,
              position: "absolute",
              top: 0,
              left: 0,
              opacity,
            }}
            htmlContent={htmlForThisStoryboard}
            baseUrl={domainFromStoryboardUrl}
            allowInteractions={
              canvasItemState.isInteractable && !isDriveMode && !isTempDragging
            }
            storyboardId={storyboard.id}
            hideOverflow={true}
            storyboardType={storyboard.type}
            outlineSystemRef={outlineSystemRef}
            // For now - can only drop one item into another storyboard
            forceDragOver={
              canvasItemState.numDraggingItems === 1 &&
              !canvasItemState.draggingThisItem
            }
            simpleCanvasRef={simpleCanvasRef}
            isResizing={isResizing}
            hideIframe={!canvasItemState.isVisible}
            resizeOnceRendered={storyboard.resizeOnceRendered}
          />
        );
      }

      // Fallback: empty div to maintain dimensions
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            backgroundColor: "white",
          }}
        />
      );
    };

    // ============================================================================
    // LOADING STATE LOGIC
    // ============================================================================

    const noContentLoaded = !storyboardUrl && !htmlForThisStoryboard;
    const noNavTree = !navTreeForStoryboard;

    const storyboardGenerating =
      storyboard.renderStatus === "generating" ||
      storyboard.renderStatus === "placeholder";

    const isTempDragging = storyboard.id === TEMP_ELEMENT_DRAG_STORYBOARD_ID;

    const showLoading =
      (registeredStoryboard?.showManualLoading ||
        noContentLoaded ||
        noNavTree ||
        Boolean(registeredStoryboard?.needsFreshNavTreeTs)) &&
      !storyboardGenerating &&
      !isTempDragging;

    const loaderWidth = Math.min(
      60,
      (storyboard.width / 3) * 2,
      (storyboard.height / 3) * 2,
    );

    const isDraggingAbsolute = useCanvasStore(
      (state) => state.isDraggingAbsolute,
    );

    // ============================================================================
    // MAIN RENDER
    // ============================================================================

    if (isTempDragging && !isDraggingAbsolute) {
      return (
        <div
          ref={containerRef}
          style={{
            width: "100%",
            height: "100%",
            opacity: canvasItemState.canDropThisItem ? 0.4 : 0.7,
            backgroundColor: "rgba(59, 130, 246)",
          }}
        />
      );
    }

    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          opacity:
            !isDraggingAbsolute && canvasItemState.canDropThisItem ? 0.4 : 1,
        }}
        onMouseEnter={() => {
          // In drive mode, neither the canvas nor the iframe is responding to events
          // Set the hovered element key so that the drive mode system works
          if (isDriveMode) {
            dispatch(
              setHoveredElementKey(
                TempoElement.forStoryboard(storyboard.id).getKey(),
              ),
            );
          }
        }}
        onMouseLeave={() => {
          if (isDriveMode) {
            dispatch(setHoveredElementKey(null));
          }
        }}
      >
        {!isTempDragging && (
          <StoryboardControlsOverlay
            storyboard={storyboard}
            canvasItemState={canvasItemState}
            index={index}
            showDot={
              storyboardError || longLoading
                ? "red"
                : slowToLoad
                  ? "yellow"
                  : "none"
            }
            dotTooltip={
              storyboardError
                ? "Error loading storyboard, see AI chat"
                : slowToLoad
                  ? "Nav tree not yet loaded, please wait a moment..."
                  : ""
            }
            isLoading={showLoading}
            simpleCanvasRef={simpleCanvasRef}
            forceNotInteractable={canvasItemState.draggingThisItem}
          />
        )}
        {renderMainFrame()}

        {/* Inner border - below content for default state */}
        {storyboard?.type === STORYBOARD_TYPES.APPLICATION && (
          <div
            className="absolute inset-0 pointer-events-none border-2 border-secondary-300"
            style={{ zIndex: -1 }}
          />
        )}

        {/* Element selection and outline system */}
        {!isTempDragging && (
          <OutlineSystem
            ref={outlineSystemRef}
            storyboardId={storyboard.id}
            iframeRef={frozenIframeRef}
            resizeDomElement={resizeDomElement}
            storyboardWidth={storyboard.width || 0}
            storyboardHeight={storyboard.height || 0}
            storyboardX={storyboard.x || 0}
            storyboardY={storyboard.y || 0}
            htmlForStoryboard={htmlForThisStoryboard || ""}
            hideAllOutlines={isDriveMode}
            setIsResizing={setIsResizing}
            simpleCanvasRef={simpleCanvasRef}
          />
        )}

        {showLoading && !longLoading && !storyboardError && (
          // Loading spinner always blocks the iframe
          <div
            data-testid="storyboard-loading-spinner"
            className="w-full h-full absolute top-0 left-0 z-2"
          >
            <div
              className="animate-spin"
              style={{
                zIndex: 2,
                pointerEvents: "none",
                position: "absolute",
                top: `calc(50% - ${loaderWidth / 2}px)`,
                left: `calc(50% - ${loaderWidth / 2}px)`,
                boxSizing: "initial",
              }}
            >
              <Loader2
                className="max-w-none stroke-[1.5px] stroke-primary-200"
                style={{
                  width: loaderWidth,
                  height: loaderWidth,
                }}
              />
            </div>
          </div>
        )}

        {storyboardError && !isDriveMode && !isTempDragging && (
          <div className="w-full h-full absolute top-0 left-0 z-3">
            <ErrorOverlay
              message={storyboardError?.message}
              filename={storyboardError?.filename}
              lineno={storyboardError?.lineno}
              colno={storyboardError?.colno}
              stack={storyboardError?.stack}
              storyboardId={storyboard.id}
              errorType={storyboardError?.errorType}
              containerWidth={storyboard.width}
              containerHeight={storyboard.height}
            />
          </div>
        )}

        {longLoading && !isDriveMode && !storyboardError && !isTempDragging && (
          <div className="w-full h-full absolute top-0 left-0 z-3">
            <StoryboardLongLoading
              onRefresh={async () => {
                useDevServerStore
                  .getState()
                  .clearErrorsForStoryboard(storyboard.id);

                useIframePoolStore.getState().refreshStoryboard(storyboard.id);
              }}
              containerWidth={storyboard.width}
              containerHeight={storyboard.height}
              onOpenInNewTab={() => {
                window.open(storyboardUrl, "_blank")?.focus();
              }}
              onInteractToDebug={() => {
                toggleCursor();
              }}
            />
          </div>
        )}
        {/* Dark overlay on hover for non-interactable items */}
        <div
          className="absolute inset-0 bg-primary-100 transition-opacity duration-250"
          style={{
            zIndex: 4,
            pointerEvents: "none",
            opacity:
              storyboardHovered && !canvasItemState.isInteractable ? 0.04 : 0,
          }}
        />
      </div>
    );
  },
);

Storyboard.displayName = "Storyboard";
